import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  MessageSquare, 
  Users, 
  ArrowLeft,
  Mail,
  Phone,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  User,
  X
} from 'lucide-react';
import { api } from '../../utils/api';

const Feedback = () => {
  const queryClient = useQueryClient();

  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const [developerOnly, setDeveloperOnly] = useState(() => {
    return localStorage.getItem('feedback-developerOnly') || 'Exclude';
  });

  const [readStatus, setReadStatus] = useState(() => {
    return localStorage.getItem('feedback-readStatus') || 'all';
  });

  const [page, setPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    localStorage.setItem('feedback-developerOnly', developerOnly);
  }, [developerOnly]);

  useEffect(() => {
    localStorage.setItem('feedback-readStatus', readStatus);
  }, [readStatus]);

  const handleApiError = (error, fallbackMessage = 'Failed to load data') => {
    const message = error?.response?.data?.message || fallbackMessage;
    toast.error(message);
  };

  const handleDeveloperOnlyChange = (value) => {
    setDeveloperOnly(value);
    setSelectedFeedback(null);
    setPage(1);
  };

  const handleReadStatusChange = (value) => {
    setReadStatus(value);
    setSelectedFeedback(null);
    setPage(1);
  };

  const DeveloperFilter = () => (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Developer</span>
        <select
          value={developerOnly}
          onChange={(e) => handleDeveloperOnlyChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
        >
          <option value="Include">Include All</option>
          <option value="Exclude">Exclude Developers</option>
          <option value="Only">Only Developers</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Status</span>
        <select
          value={readStatus}
          onChange={(e) => handleReadStatusChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
        >
          <option value="all">All</option>
          <option value="read">Read</option>
          <option value="unread">Unread</option>
        </select>
      </div>
    </div>
  );

  const { data: feedbacksData, isLoading: feedbacksLoading } = useQuery(
    ['all-feedbacks', developerOnly, readStatus, page],
    () => api.get('/feedback', { params: { limit, page, developerOnly, readStatus: readStatus !== 'all' ? readStatus : undefined } }),
    {
      staleTime: 30000,
      retry: 2,
      onError: (error) => handleApiError(error, 'Unable to load feedbacks')
    }
  );

  const feedbacks = feedbacksData?.data?.data?.feedbacks || [];
  const totalPages = feedbacksData?.data?.data?.pagination?.total || 1;
  const totalCount = feedbacksData?.data?.data?.pagination?.totalRecords || 0;

  const handleOpenFeedback = async (feedback) => {
    try {
      // Mark as read on the server if not already marked
      if (!feedback.read && feedback._id) {
        await api.patch(`/feedback/${feedback._id}/read`, { read: true });
        queryClient.invalidateQueries(['all-feedbacks']);
        setSelectedFeedback({ ...feedback, read: true });
        return;
      }
      setSelectedFeedback(feedback);
    } catch (error) {
      handleApiError(error, 'Unable to mark feedback read');
      setSelectedFeedback(feedback);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFeedbackList = () => (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 mb-6 p-6">
        <div className="items-center space-y-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Feedback by Screen
            </h1>
            <p className="text-sm text-gray-500">
              All user feedback submissions
            </p>
          </div>
        </div>

        <DeveloperFilter />
      </div>

      <div className="card">
        <div className="card-body">
          {feedbacksLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : feedbacks.length === 0 ? (
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">No feedback found</h3>
              <p className="empty-state-description">
                No feedback has been submitted yet.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {feedbacks.map((feedback, index) => (
                  <div
                    key={feedback._id || index}
                    onClick={() => handleOpenFeedback(feedback)}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-sm font-medium text-gray-900">
                            {feedback.passengerName || 'Anonymous User'}
                          </h4>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {feedback.passengerPhone && (
                              <span className="flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {feedback.passengerPhone}
                              </span>
                            )}
                            {feedback.email && (
                              <span className="flex items-center">
                                <Mail className="h-3 w-3 mr-1" />
                                {feedback.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {feedback.screen}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(feedback.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-gray-700">
                  Showing {feedbacks.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, totalCount)} of {totalCount} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderFeedbackDetails = () => {
    if (!selectedFeedback) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-full overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Feedback Details</h3>
            <button
              onClick={() => setSelectedFeedback(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-auto max-h-96">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Screen</label>
                  <p className="text-sm text-gray-900">{selectedFeedback.screen}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Date</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedFeedback.createdAt)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Passenger Name</label>
                  <p className="text-sm text-gray-900">{selectedFeedback.passengerName || 'Anonymous'}</p>
                </div>
                
                {selectedFeedback.email && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email</label>
                    <p className="text-sm text-gray-900">{selectedFeedback.email}</p>
                  </div>
                )}

                {selectedFeedback.passengerPhone && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-sm text-gray-900">{selectedFeedback.passengerPhone}</p>
                  </div>
                )}

                {selectedFeedback.tripDistanceKm && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Trip Distance</label>
                    <p className="text-sm text-gray-900">{selectedFeedback.tripDistanceKm} km</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {selectedFeedback.goodThings && (
                  <div>
                    <label className="text-sm font-medium text-green-700 flex items-center">
                      <ThumbsUp className="h-4 w-4 mr-1" />
                      Good Things
                    </label>
                    <p className="text-sm text-gray-900 bg-green-50 p-3 rounded">{selectedFeedback.goodThings}</p>
                  </div>
                )}

                {selectedFeedback.badThings && (
                  <div>
                    <label className="text-sm font-medium text-red-700 flex items-center">
                      <ThumbsDown className="h-4 w-4 mr-1" />
                      Issues
                    </label>
                    <p className="text-sm text-gray-900 bg-red-50 p-3 rounded">{selectedFeedback.badThings}</p>
                  </div>
                )}

                {selectedFeedback.improvements && (
                  <div>
                    <label className="text-sm font-medium text-yellow-700 flex items-center">
                      <Lightbulb className="h-4 w-4 mr-1" />
                      Improvements
                    </label>
                    <p className="text-sm text-gray-900 bg-yellow-50 p-3 rounded">{selectedFeedback.improvements}</p>
                  </div>
                )}

                {selectedFeedback.issueMessage && (
                  <div>
                    <label className="text-sm font-medium text-orange-700 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Issue Message
                    </label>
                    <p className="text-sm text-gray-900 bg-orange-50 p-3 rounded">{selectedFeedback.issueMessage}</p>
                  </div>
                )}
              </div>
            </div>

            {(selectedFeedback.tripStart || selectedFeedback.tripEnd) && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Trip Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedFeedback.tripStart && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Start Location</label>
                      <p className="text-sm text-gray-900">{selectedFeedback.tripStart.address}</p>
                      {selectedFeedback.tripStart.coords && (
                        <p className="text-xs text-gray-500">
                          {selectedFeedback.tripStart.coords[1]}, {selectedFeedback.tripStart.coords[0]}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedFeedback.tripEnd && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">End Location</label>
                      <p className="text-sm text-gray-900">{selectedFeedback.tripEnd.address}</p>
                      {selectedFeedback.tripEnd.coords && (
                        <p className="text-xs text-gray-500">
                          {selectedFeedback.tripEnd.coords[1]}, {selectedFeedback.tripEnd.coords[0]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {renderFeedbackList()}
      {renderFeedbackDetails()}
    </div>
  );
};

export default Feedback;
